import React, { useContext } from "react";

import { useMemo, useState, useEffect, useCallback, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { ISalesChannel } from "../../services/ecommerceApi/SalesChannel/types";
import { addToCart, getOrder } from "../../store/order";
import { graphql, useStaticQuery } from "gatsby";
import { OrderType } from "../../types";
import { fetchAllCurrencyCodes } from "../../store/currencyCode";
import { IShippingHoliday } from "../../utils/addWorkingDays";
import ecommerceApi from "../../services/ecommerceApi";
import { DateTime } from "luxon";

type IEcommerceContext = {
    salesChannel: ISalesChannel;
    flyInOpen: boolean;
    setFlyInOpen: (open: boolean) => void;
    postcode: string | undefined;
    setPostcode: (postcode: string) => void;
    editPostcode: boolean;
    setEditPostcode: (edit: boolean) => void;
    customerDetailsEmail: string | undefined;
    setCustomerDetailsEmail: (email: string | undefined) => void;
    addItemToCart: (skuId: string, quantity: number) => Promise<OrderType | undefined>;
    loqateConfig?: {
        property: string;
        propertyId: string;
        value: string;
    }[];
    shipmentDetailsUpdating: boolean;
    setShipmentDetailsUpdating: (updating: boolean) => void;
    deliveryInfomationUpdating: boolean;
    setDeliveryInfomationUpdating: React.Dispatch<React.SetStateAction<boolean>>;
    checkoutValidation: ICheckoutValidation;
    setCheckoutValidation: React.Dispatch<React.SetStateAction<ICheckoutValidation>>;
    holidays: IShippingHoliday[];
    loadHolidays: () => Promise<void>;
};

// ============== CONTEXT ==============
export const EcommerceContext = createContext({} as IEcommerceContext);

// ============== PROVIDER ==============
type Props = {
    children: React.ReactNode;
};

export function EcommerceProvider({ children }: Props) {
    const { salesChannel, loqate } = useStaticQuery<QueryType>(QUERY);
    const loqateConfig = loqate?.nodes?.[0]?.config;

    const myAddresses = useSelector((state: RootState) => state.order.addresses);
    const orderAddresses = useSelector((state: RootState) => state.order.order?.shippingAddress);
    const orderId = useSelector((state: RootState) => state.order.order?.id);
    const currencyCode = useSelector(
        (state: RootState) => state.currencyCode?.selectedCurrencyCode?.currencyCode
    );
    const dispatch = useDispatch<AppDispatch>();

    const [holidays, setHolidays] = useState<IShippingHoliday[]>([]);

    const [customerDetailsEmail, setCustomerDetailsEmail] = useState<string | undefined>(undefined);
    const [flyInOpen, setFlyInOpen] = useState<boolean>(false);
    const [internalPostcode, setInternalPostcode] = useState<string | undefined>(undefined);
    const [editPostcode, setEditPostcode] = useState(false);

    const [deliveryInfomationUpdating, setDeliveryInfomationUpdating] = useState(false);
    const [shipmentDetailsUpdating, setShipmentDetailsUpdating] = useState(false);
    const [checkoutValidation, setCheckoutValidation] = useState<{
        [key: string]: ICheckoutValidationItem;
    }>({});

    const refreshOrder = useCallback(async () => {
        if (!orderId || !dispatch || !getOrder) {
            return;
        }
        await dispatch(getOrder(orderId));
    }, [orderId, dispatch, getOrder]);

    useEffect(() => {
        refreshOrder();
    }, [refreshOrder]);

    const postcode = useMemo(() => {
        if (internalPostcode) {
            return internalPostcode;
        }

        if (orderAddresses?.length) {
            return orderAddresses[0].postCode;
        }

        if (myAddresses.length) {
            return myAddresses[0].postCode;
        }

        return salesChannel?.defaultPostcode;
    }, [salesChannel?.defaultPostcode, internalPostcode, myAddresses]);

    const loadHolidays = useCallback(async () => {
        const [thisYear, nextYear] = await Promise.all([
            await ecommerceApi.getShippingHolidaysWithTenant(`${DateTime.now().year}`),
            await ecommerceApi.getShippingHolidaysWithTenant(`${DateTime.now().year + 1}`)
        ]);
        if (!thisYear) {
            return;
        }
        setHolidays([...thisYear, ...nextYear]);
    }, []);

    const initialize = useCallback(async () => {
        const lsPostcode = localStorage.getItem("ecomHelper:postcode");
        if (lsPostcode) {
            setInternalPostcode(lsPostcode);
        }
        loadHolidays();
    }, [loadHolidays]);

    const setPostcode = useCallback(
        (postcode: string) => {
            setInternalPostcode(postcode);
        },
        [postcode]
    );

    const addItemToCart = useCallback(
        async (skuId: string, quantity: number) => {
            const { payload } = (await dispatch(
                addToCart({
                    orderId,
                    skuId,
                    quantity
                })
            )) as { payload: OrderType | undefined };
            const success =
                payload && payload.id && (!payload.errors || payload.errors.length === 0);
            if (success) {
                setFlyInOpen(true);
            }
            return payload;
        },
        [orderId, dispatch, addToCart]
    );

    // init
    useEffect(() => {
        initialize();
    }, [initialize]);

    // set internalPostcode to localStorage
    useEffect(() => {
        if (!internalPostcode) {
            return;
        }
        localStorage.setItem("ecomHelper:postcode", internalPostcode);
    }, [internalPostcode]);

    // fetch currency codes
    useEffect(() => {
        if (currencyCode) {
            return;
        }
        dispatch(fetchAllCurrencyCodes());
    }, [currencyCode, dispatch]);

    const memoizedValue: IEcommerceContext = useMemo(
        () => ({
            salesChannel,
            flyInOpen,
            setFlyInOpen,
            postcode,
            setPostcode,
            editPostcode,
            setEditPostcode,
            customerDetailsEmail,
            setCustomerDetailsEmail,
            addItemToCart,
            loqateConfig,
            shipmentDetailsUpdating,
            setShipmentDetailsUpdating,
            checkoutValidation,
            setCheckoutValidation,
            deliveryInfomationUpdating,
            setDeliveryInfomationUpdating,
            holidays,
            loadHolidays
        }),
        [
            salesChannel,
            flyInOpen,
            setFlyInOpen,
            postcode,
            setPostcode,
            editPostcode,
            setEditPostcode,
            customerDetailsEmail,
            setCustomerDetailsEmail,
            addItemToCart,
            loqateConfig,
            shipmentDetailsUpdating,
            setShipmentDetailsUpdating,
            checkoutValidation,
            setCheckoutValidation,
            deliveryInfomationUpdating,
            setDeliveryInfomationUpdating,
            holidays,
            loadHolidays
        ]
    );

    if (!salesChannel) {
        return <></>;
    }

    return <EcommerceContext.Provider value={memoizedValue}>{children}</EcommerceContext.Provider>;
}

type QueryType = {
    salesChannel: ISalesChannel;
    loqate: {
        nodes: {
            id: string;
            config: LoqateConfig;
        }[];
    };
};
const QUERY = graphql`
    query {
        salesChannel {
            id
            name
            email
            logo
            logoFile {
                publicURL
            }

            defaultPostcode
            defaultCountry

            primaryColour
            secondaryColour

            phone
            address
            type
            donations {
                id
                name
                desc
                type
                value
                image
                enabled
                salesChannel
            }

            taxes {
                id
                name
                taxRates {
                    id
                    taxRate
                    zipCodeRegex
                    countryCodeRegex
                }
            }

            logoFile {
                childImageSharp {
                    gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
            }
            iconFile {
                childImageSharp {
                    gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
                publicURL
            }

            metadata
        }
        loqate: allIntegration(filter: { type: { eq: "loqate" } }) {
            nodes {
                id
                config {
                    property
                    propertyId
                    value
                }
            }
        }
    }
`;
export type LoqateConfig = {
    property: string;
    propertyId: string;
    value: string;
}[];

// ============== HOOK ==============
export const useEcommerceHelper = () => {
    const context = useContext(EcommerceContext);
    if (!context) {
        throw new Error("useEcommerceHelper context must be use inside EcommerceProvider");
    }
    return context;
};

export type ICheckoutValidation = {
    [key: string]: ICheckoutValidationItem;
};
export type ICheckoutValidationItem = {
    isInvalid: boolean;
    message?: string;
};
